import { useEffect, useRef } from "react";
import { animate, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Wifi, Cctv, ShieldCheck, Network, KeyRound, Cloud, Satellite } from "lucide-react";
import { IMG } from "./data";
import { ease, useCoarse } from "./motion";

const ORBITS = [
  { r: 0.68, k: 0.3, tilt: -16, speed: 0.2 },
  { r: 0.93, k: 0.24, tilt: 9, speed: -0.13 },
  { r: 0.52, k: 0.38, tilt: 26, speed: 0.38 },
];
const SATS = [
  { id: "wifi", label: "WI-FI 7", desc: "Seamless coverage, every room", icon: Wifi, o: 0, a: 0.4 },
  { id: "cctv", label: "CCTV", desc: "4K cameras, local recording", icon: Cctv, o: 0, a: 3.5 },
  { id: "firewall", label: "FIREWALL", desc: "Next-gen firewall & IPS", icon: ShieldCheck, o: 1, a: 1.1 },
  { id: "switch", label: "SWITCHING", desc: "PoE — one cable per device", icon: Network, o: 1, a: 3.2 },
  { id: "access", label: "ACCESS", desc: "Phone & NFC door entry", icon: KeyRound, o: 1, a: 5.1 },
  { id: "cloud", label: "SD-WAN", desc: "Multi-site links & failover", icon: Cloud, o: 2, a: 2.2 },
];
const STARLINK = { r: 1.04, k: 0.14, tilt: -7, speed: 0.08 };
const TRAIL = [0.07, 0.14];
const PACKETS = Array.from({ length: 14 }, (_, i) => ({ o: i % 3, a: (i * 2.39) % (Math.PI * 2), v: 2.2 + (i % 4) * 0.5, c: i % 4 === 0 ? "#10B981" : "#00F0FF" }));
const CORNERS = [
  { id: "wan1", x: 90, y: 70, label: "WAN 1", pos: "left-[2%] top-[3%]" },
  { id: "wan2", x: 910, y: 70, label: "WAN 2", pos: "right-[2%] top-[3%]" },
  { id: "cloud", x: 90, y: 930, label: "AZURE", pos: "left-[2%] bottom-[3%]" },
];
const COLOR = { up: "#00F0FF", standby: "#475569", down: "#F43F5E", backup: "#10B981" };

const clamp = (v) => Math.max(0, Math.min(1, v));
const pt = (o, a, m, extra = 0) => {
  const ex = o.r * m * Math.cos(a);
  const ey = o.r * m * o.k * Math.sin(a);
  const t = ((o.tilt + extra) * Math.PI) / 180;
  return { x: ex * Math.cos(t) - ey * Math.sin(t), y: ex * Math.sin(t) + ey * Math.cos(t), d: Math.sin(a) };
};
const arc = (o, m, from, to, extra) => {
  let d = "";
  for (let i = 0; i <= 48; i++) {
    const p = pt(o, from + ((to - from) * i) / 48, m, extra);
    d += `${i ? "L" : "M"}${(500 + p.x * 500).toFixed(1)},${(500 + p.y * 500).toFixed(1)}`;
  }
  return d;
};

const stateOf = (id, failover) => {
  if (id === "wan1") return failover ? "down" : "up";
  if (id === "wan2") return failover ? "backup" : "standby";
  return "up";
};
const subOf = (id, s, compact) =>
  (compact ? { down: "DOWN", backup: "5G LIVE", standby: "STANDBY" } : { down: "LINK DOWN", backup: "STARLINK / 5G ACTIVE", standby: "STARLINK / 5G STANDBY" })[s] || (id === "cloud" ? "VNET" : "FIBRE");

const useOrbit = (ready, wrap, refs, failRef) => {
  useEffect(() => {
    if (!ready) return;
    let raf;
    const t0 = performance.now();
    const loop = (now) => {
      const t = (now - t0) / 1000;
      const intro = 1 - Math.pow(1 - clamp(t / 2.8), 4);
      const m = 0.25 + 0.75 * intro;
      const spin = Math.pow(1 - intro, 2) * 5;
      const W = (wrap.current?.offsetWidth || 0) / 2;
      const small = W < 260 ? 0.76 : 1;
      const ex = Math.min(window.scrollY / 14, 18);
      ORBITS.forEach((o, i) => {
        refs.back.current[i]?.setAttribute("d", arc(o, m, Math.PI, 2 * Math.PI, ex));
        refs.front.current[i]?.setAttribute("d", arc(o, m, 0, Math.PI, ex));
      });
      const place = (el, q, alpha, scale) => {
        el.style.transform = `translate3d(${q.x * W}px, ${q.y * W}px, 0) translate(-50%, -50%) scale(${scale * small})`;
        el.style.zIndex = q.d > 0 ? 30 : 5;
        el.style.opacity = alpha;
        el.dataset.front = q.d > 0 ? "1" : "0";
      };
      SATS.forEach((s, i) => {
        const o = ORBITS[s.o];
        const q = pt(o, s.a + t * o.speed + spin * Math.sign(o.speed), m, ex);
        const f = (q.d + 1) / 2;
        const appear = clamp((t - 0.6 - i * 0.15) / 0.8);
        const el = refs.sats.current[i];
        if (el) place(el, q, appear * (0.4 + 0.6 * f), 0.7 + 0.38 * f);
        const ln = refs.links.current[i];
        if (ln) {
          ln.setAttribute("x2", 500 + q.x * 500);
          ln.setAttribute("y2", 500 + q.y * 500);
          ln.style.opacity = appear * 0.7;
        }
      });
      PACKETS.forEach((p, i) => {
        const o = ORBITS[p.o];
        const a = p.a + t * o.speed * p.v + spin * 1.5;
        const q = pt(o, a, m, ex);
        const alpha = clamp(t - 1) * (0.35 + 0.65 * ((q.d + 1) / 2));
        const el = refs.packets.current[i];
        if (el) place(el, q, alpha, 1);
        TRAIL.forEach((off, j) => {
          const tr = refs.trails.current[i * 2 + j];
          if (tr) place(tr, pt(o, a - off * Math.sign(o.speed), m, ex), alpha * (0.5 - j * 0.2), 0.8 - j * 0.2);
        });
      });
      const sq = pt(STARLINK, 2.4 + t * STARLINK.speed, m, ex * 0.5);
      if (refs.star.current) place(refs.star.current, sq, clamp(t - 1.6), 0.85 + 0.2 * ((sq.d + 1) / 2));
      if (refs.beam.current) {
        refs.beam.current.setAttribute("x1", 500 + sq.x * 500);
        refs.beam.current.setAttribute("y1", 500 + sq.y * 500);
        refs.beam.current.style.opacity = failRef.current ? 0.9 : 0;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [ready, wrap, refs, failRef]);
};

const Corner = ({ c, state, compact }) => (
  <div data-testid={`topology-node-${c.id}`} className={`absolute ${c.pos} z-40 flex items-center rounded-full border bg-void/80 backdrop-blur font-mono transition-colors duration-500 ${compact ? "gap-1.5 px-2.5 py-1 text-[8px] tracking-[0.14em]" : "gap-2 px-3 py-1.5 text-[9px] sm:text-[10px] tracking-[0.18em]"}`} style={{ borderColor: `${COLOR[state]}55`, color: COLOR[state] }}>
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: COLOR[state], boxShadow: `0 0 10px ${COLOR[state]}` }} />
    <span className="text-slate-200">{c.label}</span>
    <span>{subOf(c.id, state, compact)}</span>
  </div>
);

export const Orbit = ({ ready, failover, compact = false }) => {
  const wrap = useRef(null);
  const refs = useRef({ back: { current: [] }, front: { current: [] }, sats: { current: [] }, links: { current: [] }, packets: { current: [] }, trails: { current: [] }, star: { current: null }, beam: { current: null } }).current;
  const failRef = useRef(failover);
  failRef.current = failover;
  useOrbit(ready, wrap, refs, failRef);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 18 });
  const coarse = useCoarse();
  const onMove = (e) => {
    if (coarse) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  // No mouse on phones, so the scene drifts through a slow 3D sway on its own instead.
  useEffect(() => {
    if (!coarse || !ready) return;
    const a = animate(mx, [0, 0.42, 0, -0.42, 0], { duration: 14, repeat: Infinity, ease: "easeInOut" });
    const b = animate(my, [0, -0.3, 0, 0.3, 0], { duration: 10, repeat: Infinity, ease: "easeInOut" });
    return () => { a.stop(); b.stop(); };
  }, [coarse, ready, mx, my]);

  return (
    <div className="relative aspect-square w-full [perspective:1400px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-topology">
      <motion.div ref={wrap} className="absolute inset-0" style={{ rotateX: rx, rotateY: ry }}>
        <svg viewBox="0 0 1000 1000" className="absolute inset-0 h-full w-full z-[4] overflow-visible" aria-hidden>
          {ORBITS.map((o, i) => <path key={i} ref={(el) => (refs.back.current[i] = el)} fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="1.2" strokeDasharray={i === 2 ? "2 8" : undefined} />)}
          {CORNERS.map((c) => {
            const s = stateOf(c.id, failover);
            return <line key={`${c.id}-${s}`} x1="500" y1="500" x2={c.x} y2={c.y} stroke={COLOR[s]} strokeWidth="1.4" strokeOpacity={s === "standby" ? 0.5 : 0.8} className={s === "up" || s === "backup" ? "flow flow-in" : ""} strokeDasharray={s === "down" || s === "standby" ? "4 8" : undefined} />;
          })}
          {ready && [0, 1.3, 2.6].map((d) => <circle key={d} cx="500" cy="500" r="225" className="moon-ping" style={{ animationDelay: `${d}s` }} />)}
          <line ref={(el) => (refs.beam.current = el)} x1="500" y1="500" x2="910" y2="70" stroke="#10B981" strokeWidth="2.2" strokeDasharray="7 6" className="beam-flow" style={{ opacity: 0, transition: "opacity 0.5s", filter: "drop-shadow(0 0 4px #10B981)" }} data-testid="orbit-starlink-beam" />
          {SATS.map((s, i) => <line key={s.id} ref={(el) => (refs.links.current[i] = el)} x1="500" y1="500" x2="500" y2="500" stroke="#00F0FF" strokeWidth="1.1" className="flow" style={{ opacity: 0 }} />)}
        </svg>

        <motion.div
          className="absolute left-1/2 top-1/2 z-10 aspect-square w-[44%] -ml-[22%] -mt-[22%] rounded-full overflow-hidden moon-glow"
          initial={{ opacity: 0, scale: 0.55 }}
          animate={ready ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 2.4, ease }}
          data-testid="topology-node-gateway"
        >
          <img src={IMG.moon} alt="" aria-hidden className="h-full w-full object-cover scale-[1.42] moon-spin select-none" />
          <div className="absolute inset-0 moon-shade" />
        </motion.div>

        <svg viewBox="0 0 1000 1000" className="absolute inset-0 h-full w-full z-[20] overflow-visible pointer-events-none" aria-hidden>
          {ORBITS.map((o, i) => <path key={i} ref={(el) => (refs.front.current[i] = el)} fill="none" stroke="rgba(0,240,255,0.35)" strokeWidth="1.2" strokeDasharray={i === 2 ? "2 8" : undefined} />)}
          <defs><path id="moonRing" d="M500,500 m-262,0 a262,262 0 1,1 524,0 a262,262 0 1,1 -524,0" /></defs>
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: ready ? 1 : 0 }} transition={{ duration: 1.5, delay: 1.8 }}>
            <g className="spin-slow" style={{ transformOrigin: "500px 500px" }}>
              <text fill="rgba(148,163,184,0.5)" fontSize="14" fontFamily="JetBrains Mono, monospace" letterSpacing="6.5">
                <textPath href="#moonRing">LUNAR NETWORK SOLUTIONS · CABLE TO CLOUD · DESIGN · INSTALL · SUPPORT · CABLE TO CLOUD ·</textPath>
              </text>
            </g>
          </motion.g>
        </svg>

        {PACKETS.flatMap((p, i) => TRAIL.map((_, j) => (
          <span key={`t${i}-${j}`} ref={(el) => (refs.trails.current[i * 2 + j] = el)} className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full opacity-0" style={{ background: p.c }} />
        )))}
        <div ref={(el) => (refs.star.current = el)} className="absolute left-1/2 top-1/2 flex items-center gap-1.5 opacity-0 will-change-transform" data-testid="orbit-starlink">
          <Satellite size={16} className={failover ? "text-emerald-400" : "text-slate-300"} strokeWidth={1.5} />
          <span className={`font-mono text-[8px] tracking-[0.2em] ${failover ? "text-emerald-400" : "text-slate-500"}`}>STARLINK</span>
        </div>
        {PACKETS.map((p, i) => (
          <span key={i} ref={(el) => (refs.packets.current[i] = el)} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full opacity-0" style={{ background: p.c, boxShadow: `0 0 10px 2px ${p.c}` }} />
        ))}

        {SATS.map((s, i) => (
          <div key={s.id} ref={(el) => (refs.sats.current[i] = el)} data-testid={`orbit-sat-${s.id}`} className="orbit-sat absolute left-1/2 top-1/2 flex flex-col items-center opacity-0 will-change-transform">
            <span className="sat-icon grid h-11 w-11 sm:h-12 sm:w-12 place-items-center rounded-full border border-cyan-300/60 bg-[#0B0F17]/90 text-cyan-300 shadow-[0_0_24px_rgba(0,240,255,0.35)] transition-[background-color,box-shadow,color] duration-300">
              <s.icon size={18} strokeWidth={1.6} />
            </span>
            <span className="sat-label mt-2 whitespace-nowrap font-mono text-[8px] sm:text-[9px] tracking-[0.2em] text-slate-200">{s.label}</span>
            <span className="sat-tip pointer-events-none absolute top-full mt-7 whitespace-nowrap rounded-full border border-cyan-300/40 bg-void/90 px-3 py-1 text-[11px] text-slate-200 backdrop-blur">{s.desc}</span>
          </div>
        ))}

        {CORNERS.map((c) => <Corner key={c.id} c={c} state={stateOf(c.id, failover)} compact={compact} />)}
      </motion.div>
    </div>
  );
};
